import { nanoid } from "nanoid";
import { z } from "zod";

import { getAuthStore } from "~/modules/auth/auth-store";
import { getConfigStore } from "~/stores/config";

export type KnownApiHost = "cap-srv" | "web-core" | "file-upload";

export type ApiHost = KnownApiHost | { type: "custom"; host: string };

interface ApiRequestParams {
  host: ApiHost;

  path: string;
  method: "GET" | "POST" | "PUT" | "DELETE";

  data?: Record<string, unknown>;
  headers?: Record<string, string>;

  clientTrace?: string;

  signal?: AbortSignal;

  assumeUnAuthenticated?: boolean;
}

export const createClientTrace = () => `web/${nanoid()}`;

const responseTypeToMethod = {
  "application/json": "json",
  "text/plain": "text",
  "text/html": "text",
  "application/octet-stream": "blob",
} as const;

export interface ApiResponse {
  ok: boolean;
  data: unknown;
  status: number;
}

export async function makeApiRequest({
  host,
  path,
  method,
  data,
  headers: userHeaders,
  clientTrace,
  signal,
  assumeUnAuthenticated = false,
}: ApiRequestParams): Promise<ApiResponse> {
  const configStore = getConfigStore();
  const hosts: Record<KnownApiHost, string> = {
    "cap-srv": configStore.env.CAP_SRV_API_URL,
    "web-core": configStore.env.WEB_CORE_API_URL,
    "file-upload": configStore.env.FILE_UPLOAD_API_URL,
  };
  const urlHost = typeof host === "object" ? host.host : hosts[host];

  const url = new URL(path, urlHost);

  const headers: Record<string, string> = {
    ...userHeaders,
  };

  if (!assumeUnAuthenticated) {
    const accessToken = await getAuthStore().getAccessToken();
    if (accessToken) {
      headers["Authorization"] = `Bearer ${accessToken}`;
    }
  }

  if (host === "cap-srv" || host === "web-core") {
    headers["x-captions-user-timezone"] = Intl.DateTimeFormat().resolvedOptions().timeZone;
    headers["x-captions-client-trace"] = clientTrace ?? createClientTrace();
  }

  let body: BodyInit | undefined;
  if (method === "POST" || method === "PUT") {
    if (data instanceof FormData) {
      body = data;
    } else if (data) {
      body = JSON.stringify(data);
      headers["content-type"] = "application/json";
    }
  }

  const response = await fetch(url, { method, signal, headers, body });
  const contentType = response.headers.get("content-type") ?? "application/json";
  const mediaType = contentType.split(";")[0].trim().toLowerCase();
  const parseFunc = responseTypeToMethod[mediaType as keyof typeof responseTypeToMethod] ?? "json";
  const responseData = await response[parseFunc]();

  const parsedResponse = deserializeResponseFromHost(host, responseData, mediaType);

  return { data: parsedResponse, status: response.status, ok: response.ok };
}

function deserializeResponseFromHost(host: ApiHost, data: unknown, contentType: string) {
  if (host === "cap-srv" && contentType === "application/json") {
    const { success, ...rest } = baseCapSrvResponseSchema.parse(data).result;

    if (success) {
      return rest.data;
    }

    return rest;
  }

  return data;
}

export const baseCapSrvResponseSchema = z
  .object({
    result: z
      .object({
        data: z.any().optional(),
        success: z.boolean(),
      })
      .passthrough(),
  })
  .passthrough();
